import { useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type TableOptions,
} from '@tanstack/react-table';

import './DataTable.css';

/**
 * DataTable — thin styling wrapper over TanStack Table v8 (ADR-0013 §Tables).
 *
 * Intentionally small: no slot for toolbars, pagination, virtualization. Each
 * screen keeps its own filter/search chrome; this component only renders the
 * `<table>` with Sentinel's shared `.sentinel-table` class. When a screen
 * needs extra presentation (loading, error, empty), it passes the matching
 * state flags in and we render the correct element.
 *
 * Usage:
 *   ```tsx
 *   <DataTable
 *     data={rows}
 *     columns={columns}
 *     ariaLabel="Scenarios"
 *     loading={isLoading}
 *     error={error}
 *     emptyMessage="No scenarios match the current filters."
 *     getRowId={(row) => row.scenarioId}
 *   />
 *   ```
 */
export interface DataTableProps<TRow> {
  data: TRow[];
  columns: ColumnDef<TRow, unknown>[];
  /** Accessible name for the table (read by SR, used by tests via role). */
  ariaLabel: string;
  /** Optional caption rendered for screen readers only. */
  caption?: string;
  /** `data-testid` for integration with RTL without grepping the DOM. */
  testId?: string;
  /** Whether the caller is still fetching. Shown as a polite status row. */
  loading?: boolean;
  /** Set by React Query when the fetch rejected. */
  error?: unknown;
  /** Rendered when `data` is `[]` and not loading. */
  emptyMessage?: string;
  /** Test id for the empty state (defaults to `<testId>-empty`). */
  emptyTestId?: string;
  /** Test id for the error state (defaults to `<testId>-error`). */
  errorTestId?: string;
  /**
   * Row-key accessor. Passed through to TanStack Table's `getRowId` so row
   * identity survives refetches. Falls back to the row index.
   */
  getRowId?: TableOptions<TRow>['getRowId'];
  /** Extra wrapper class — lets individual screens tweak surrounding layout. */
  className?: string;
  /** Click handler on `<tr>` for master-detail flows. Optional. */
  onRowClick?: (row: Row<TRow>) => void;
}

export function DataTable<TRow>(props: DataTableProps<TRow>) {
  const {
    data,
    columns,
    ariaLabel,
    caption,
    testId,
    loading = false,
    error,
    emptyMessage = 'No rows match the current filters.',
    emptyTestId,
    errorTestId,
    getRowId,
    className,
    onRowClick,
  } = props;

  // `data` is a stable reference in React Query results, but memoise anyway
  // so upstream callers can pass a fresh literal without remounting headers.
  const stableData = useMemo(() => data, [data]);
  const stableColumns = useMemo(() => columns, [columns]);

  const table = useReactTable({
    data: stableData,
    columns: stableColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  const finalEmptyTestId = emptyTestId ?? (testId ? `${testId}-empty` : undefined);
  const finalErrorTestId = errorTestId ?? (testId ? `${testId}-error` : undefined);
  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;

  const wrapperClass = ['data-table-wrap', className].filter(Boolean).join(' ');

  if (error) {
    return (
      <div className={wrapperClass}>
        <div
          role="alert"
          className="data-table-error"
          data-testid={finalErrorTestId}
        >
          Couldn&apos;t load results. {errorMessage(error)}
        </div>
      </div>
    );
  }

  if (loading && !hasRows) {
    return (
      <div className={wrapperClass}>
        <div role="status" aria-live="polite" className="data-table-status">
          Loading…
        </div>
      </div>
    );
  }

  if (!hasRows) {
    return (
      <div className={wrapperClass}>
        <div
          className="data-table-empty"
          data-testid={finalEmptyTestId}
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <table
        className="sentinel-table"
        aria-label={ariaLabel}
        {...(testId ? { 'data-testid': testId } : {})}
      >
        {caption && <caption className="visually-hidden">{caption}</caption>}
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  style={header.column.columnDef.meta && (header.column.columnDef.meta as { align?: string }).align === 'right' ? { textAlign: 'right' } : undefined}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'data-table-row-clickable' : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Please retry.';
}
